import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type FileFormatOption = { value: string; label: string };

/** Dataset type keys used in forms (must match API / catalogue values). */
export const DATASET_TYPE_STRUCTURED = 'Structured';
export const DATASET_TYPE_SEMI_STRUCTURED = 'semi Structured';
export const DATASET_TYPE_UNSTRUCTURED = 'unStructured';

/** Dataset type dropdown options: API `value` + user-facing `label`. */
export const DATASET_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: DATASET_TYPE_STRUCTURED, label: 'Structured' },
  { value: DATASET_TYPE_SEMI_STRUCTURED, label: 'Semi Structured' },
  { value: DATASET_TYPE_UNSTRUCTURED, label: 'Unstructured' },
];

/** Map stored/API dataset type to a consistent display label. */
export function getDatasetTypeDisplayLabel(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }
  const exact = DATASET_TYPE_OPTIONS.find((opt) => opt.value === raw);
  if (exact) {
    return exact.label;
  }
  const ci = DATASET_TYPE_OPTIONS.find(
    (opt) =>
      opt.value.toLowerCase() === raw.toLowerCase() ||
      opt.label.toLowerCase() === raw.toLowerCase(),
  );
  return ci?.label ?? raw;
}

const DOCUMENTS_BASE = '/documents';

@Injectable({
  providedIn: 'root'
})
export class FileFormatConfigService {
  private cache: {
    datasetStructured?: FileFormatOption[];
    datasetSemiStructured?: FileFormatOption[];
    datasetUnstructured?: FileFormatOption[];
    model?: FileFormatOption[];
  } = {};

  constructor(private http: HttpClient) {}

  /** Convert array of label strings to options (value = label). */
  private toOptions(labels: string[]): FileFormatOption[] {
    if (!Array.isArray(labels)) return [];
    return labels.map(l => ({ value: l, label: l }));
  }

  private async loadJson(path: string): Promise<string[]> {
    try {
      const data = await firstValueFrom(this.http.get<string[]>(path));
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.warn(`FileFormatConfigService: failed to load ${path}`, err);
      return [];
    }
  }

  /** Get file format options for dataset type: Structured, semi Structured, unStructured. */
  async getDatasetFileFormats(datasetType: string): Promise<FileFormatOption[]> {
    if (datasetType === DATASET_TYPE_STRUCTURED) {
      if (!this.cache.datasetStructured) {
        const labels = await this.loadJson(`${DOCUMENTS_BASE}/dataset-structured-fileformat.json`);
        this.cache.datasetStructured = this.toOptions(labels);
      }
      return this.cache.datasetStructured;
    }
    if (datasetType === DATASET_TYPE_SEMI_STRUCTURED) {
      if (!this.cache.datasetSemiStructured) {
        const labels = await this.loadJson(`${DOCUMENTS_BASE}/dataset-semi-structured-fileformat..json`);
        this.cache.datasetSemiStructured = this.toOptions(labels);
      }
      return this.cache.datasetSemiStructured;
    }
    if (datasetType === DATASET_TYPE_UNSTRUCTURED) {
      if (!this.cache.datasetUnstructured) {
        const labels = await this.loadJson(`${DOCUMENTS_BASE}/dataset-unstructured-fileformat..json`);
        this.cache.datasetUnstructured = this.toOptions(labels);
      }
      return this.cache.datasetUnstructured;
    }
    return [];
  }

  /** Get file format options for model upload (from model-fileformats.json). */
  async getModelFileFormats(): Promise<FileFormatOption[]> {
    if (!this.cache.model) {
      const labels = await this.loadJson(`${DOCUMENTS_BASE}/model-fileformats.json`);
      this.cache.model = this.toOptions(labels);
    }
    return this.cache.model;
  }

  /**
   * Load all dataset format files and return a map by dataset type.
   * Use this when you need the full map for dataset contribution.
   */
  async loadDatasetFileFormatMap(): Promise<{ [key: string]: FileFormatOption[] }> {
    const [structured, semiStructured, unstructured] = await Promise.all([
      this.getDatasetFileFormats(DATASET_TYPE_STRUCTURED),
      this.getDatasetFileFormats(DATASET_TYPE_SEMI_STRUCTURED),
      this.getDatasetFileFormats(DATASET_TYPE_UNSTRUCTURED)
    ]);
    return {
      [DATASET_TYPE_STRUCTURED]: structured,
      [DATASET_TYPE_SEMI_STRUCTURED]: semiStructured,
      [DATASET_TYPE_UNSTRUCTURED]: unstructured
    };
  }

  /**
   * Match API / template casing (e.g. csv) to a predefined dropdown option (e.g. CSV).
   */
  matchFileFormatOption(
    fileFormat: string,
    options: FileFormatOption[]
  ): FileFormatOption | undefined {
    const trimmed = String(fileFormat ?? '').trim();
    if (!trimmed || !options.length) {
      return undefined;
    }
    return options.find(
      (o) =>
        o.value === trimmed ||
        o.value.toLowerCase() === trimmed.toLowerCase() ||
        o.label.toLowerCase() === trimmed.toLowerCase()
    );
  }

  /** Resolve stored/API fileFormat to the option value used in forms. */
  resolveFileFormatForForm(fileFormat: string, options: FileFormatOption[]): string {
    const match = this.matchFileFormatOption(fileFormat, options);
    return match?.value ?? String(fileFormat ?? '').trim();
  }
}
